import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Ticker } from '@/components/common/Ticker/Ticker';
import {
  StringMain,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { TelegramStarIcon } from '@/components/ui/Icons';

import styles from './styles.module.scss';

import goldPng from '/img/gold.png';
import tonsPerUsdImg from '/img/shop/tonsPerUsd.png';

interface Props {
  price: number;
  discountPrice?: number;
  sale?: number;
  days?: number;
  hours?: number;
  label?: string;
  active?: boolean;
  onClick: () => void;
  type?: 'usdt' | 'star' | 'ton' | 'gold';
  variant?: 'solid' | 'transparent';
}

const SelectPriceCard: FC<Props> = ({
  discountPrice,
  price,
  days,
  hours,
  active,
  label,
  sale,
  onClick,
  type,
  variant = 'transparent',
}) => {
  const { t } = useTranslation();

  const getIcon = (): ReactNode => {
    switch (type) {
      case 'usdt':
        return '$';
      case 'star':
        return <TelegramStarIcon className="size-4 !text-gold" />;

      case 'ton':
        return <img src={tonsPerUsdImg} className="size-4" />;
      case 'gold':
        return <img src={goldPng} className="size-4" />;
    }
  };

  return (
    <div
      role="button"
      className={classNames(styles.wrapper, styles[variant])}
      onClick={onClick}
    >
      {!!sale && (
        <div className="absolute -top-[4px] right-0 z-[4]">
          <Ticker
            variant="gold-small"
            className="last:[&>span]:hidden"
            text={`-${sale}%`}
          />
        </div>
      )}
      <div className={classNames(styles.card, active && styles.active)}>
        <StringSmall
          weight={'bold'}
          className={classNames(
            'capitalize mb-auto',
            active && 'text-[#82E024]',
          )}
        >
          {label
            ? label
            : days
              ? `${days} ${t('days')}`
              : `${hours} ${t('hours')}`}
        </StringSmall>

        {price === 0 ? (
          <StringMain weight="extrabold" className="uppercase">
            {t('Free')}
          </StringMain>
        ) : (
          <>
            {!!discountPrice && price !== discountPrice && (
              <div className="relative block leading-none">
                <StringXSmall className="!text-white/40 !leading-none flex items-center">
                  {getIcon()} {price}
                </StringXSmall>
                <span className={styles.redLine} />
              </div>
            )}
            <StringMain
              weight="extrabold"
              className="!leading-none flex gap-0.5 items-center"
            >
              {getIcon()} {discountPrice ?? price}
            </StringMain>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectPriceCard;
